import { AggregationType, VISUALIZATION_TYPE } from "~/ui-rtk/constants/charts"
import { createElement, ElementType, useEffect, useMemo } from "react"
import useConnect from "./connect"
import { TableSkeleton } from "../TableSkeleton"
import { RowSelectionState, SortingState } from "@tanstack/react-table"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import ChartSkeleton from "../ChartSkeleton"

type TChartDataLoaderProps = {
  component: ElementType
  chartId: string
  dateRange: [Date, Date]
  granularity: AggregationType
  className?: string
  widgetType: VISUALIZATION_TYPE
  setCacheItem: (
    chartId: string,
    dateRange: [Date, Date],
    granularity: AggregationType,
    data: {
      dataSource: Record<string, unknown>[] | Record<string, unknown>[][]
      columns: Record<string, unknown>
      totalPages?: number | null
    },
    page: number,
    itemsPerPage: number,
    cubeFilters?: TCubeFilterOptions,
    sorting?: SortingState,
    cubeQueryParams?: Record<string, string>,
  ) => void
  id?: string
  page: number
  itemsPerPage: number
  enablePagination?: boolean
  cubeFilters?: TCubeFilterOptions
  sorting?: SortingState
  extraSorting?: Array<{ id: string; desc: boolean }>
  disableGranularity?: boolean
  prefixEnabled?: boolean
  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void
  cubeQueryParams?: Record<string, string>
}

export default function ChartDataLoader({
  component,
  chartId,
  dateRange,
  className,
  granularity,
  setCacheItem,
  widgetType,
  page,
  itemsPerPage,
  cubeFilters,
  sorting,
  extraSorting,
  enablePagination = false,
  disableGranularity = false,
  prefixEnabled = false,
  cubeQueryParams,
  ...props
}: TChartDataLoaderProps) {
  const { dataSource, columns, totalPages } = useConnect({
    chartId,
    granularity,
    dateRange,
    page,
    itemsPerPage,
    cubeFilters,
    sorting,
    extraSorting,
    enablePagination,
    disableGranularity,
    cubeQueryParams,
    prefixEnabled,
  })

  useEffect(() => {
    if (dataSource && columns) {
      setCacheItem(
        chartId,
        dateRange,
        granularity,
        {
          dataSource,
          columns: columns as unknown as Record<string, unknown>,
          totalPages,
        },
        page,
        itemsPerPage,
        cubeFilters,
        sorting,
        cubeQueryParams,
      )
    }
  }, [
    dataSource,
    columns,
    chartId,
    dateRange,
    granularity,
    setCacheItem,
    page,
    totalPages,
    itemsPerPage,
    cubeFilters,
    sorting,
    cubeQueryParams,
  ])

  const chartProps = useMemo(
    () => ({
      ...props,
      className,
      isLoading: true,
      enableRowSelection: props.enableRowSelection,
      rowSelection: props.rowSelection,
      onRowSelectionChange: props.onRowSelectionChange,
    }),
    [props, className],
  )

  if (
    [
      VISUALIZATION_TYPE.TableVisualization,
      VISUALIZATION_TYPE.SimpleTable,
    ].includes(widgetType)
  ) {
    return <TableSkeleton />
  }
  if (
    [
      VISUALIZATION_TYPE.ComposedChart,
      VISUALIZATION_TYPE.AreaChart,
      VISUALIZATION_TYPE.BarChart,
    ].includes(widgetType)
  ) {
    return createElement(component, chartProps)
  }
  return <ChartSkeleton className={className} />
}
